import { AddIcon } from '@chakra-ui/icons';
import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Button,
  Text,
  IconButton,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Select,
  Switch,
  Badge,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Box,
  useToast,
} from '@chakra-ui/react';
import Tooltip from '../../../../../../../components/Tooltip';
import { useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { handleIsInvalidField } from '~/utils/general';
import firebaseFirestore from '~/config/firebase/firestore';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { Location } from '~/core/types';
import { useActivityLogs } from '~/hooks/useActivityLogs';
import { useAuth } from '~/hooks/useAuth';

export type Company = {
  name: string;
  id: string;
};

const LocationsSettings = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany] = useState<Company>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedUser] = useState();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const onSnaphotRef = useRef<any>();

  const { logActivity } = useActivityLogs();
  const { user } = useAuth();
  const toast = useToast();
  /** Reservas futuras por sede: avisa antes de cerrar una que aún tiene gente. */
  const [proximas, setProximas] = useState<Record<string, number>>({});
  const [cargandoProximas, setCargandoProximas] = useState(false);
  const [cambiando, setCambiando] = useState<string>('');
  const [porConfirmar, setPorConfirmar] = useState<Location | null>(null);

  /**
   * Cuenta las reservas que todavía no han ocurrido, agrupadas por sede.
   *
   * Se piden por `startDatetime` a secas y se agrupan aquí: filtrar además por
   * sede exigiría un índice compuesto por cada consulta. En producción son unas
   * 75 en total, así que sale barato.
   */
  const contarProximas = async () => {
    setCargandoProximas(true);
    try {
      const snap = await getDocs(
        query(
          collection(firebaseFirestore, 'reservations'),
          where('startDatetime', '>=', Timestamp.fromDate(new Date()))
        )
      );
      const cuenta: Record<string, number> = {};
      snap.docs.forEach((d) => {
        const r = d.data() as any;
        if (r.status === 'cancelled') return;
        const id = r.location?.id;
        if (id) cuenta[id] = (cuenta[id] ?? 0) + 1;
      });
      setProximas(cuenta);
    } finally {
      setCargandoProximas(false);
    }
  };

  /**
   * Abre o cierra una sede para nuevas reservas.
   *
   * Solo toca el campo `status`. Las reservas que ya existen no se tocan, y los
   * informes las siguen contando porque leen la colección de reservas, no la de
   * sedes. Lo único que cambia es que deja de ofrecerse en el formulario y de
   * aparecer en el panel del cliente.
   */
  const cambiarEstado = async (location: Location, activar: boolean) => {
    setCambiando(location.id);
    try {
      await updateDoc(doc(firebaseFirestore, 'locations', location.id), {
        status: activar ? 'active' : 'inactive',
      });
      logActivity({
        activityType: 'location_status_change',
        entityType: 'location',
        entityId: location.id,
        companyId: company?.id,
        details: {
          sede: location.name,
          negocio: company?.name,
          estado: activar ? 'activa' : 'inactiva',
          reservasProximas: proximas[location.id] ?? 0,
          porQuien: user?.email ?? '',
          momento: new Date().toISOString(),
        },
      });
      toast({
        status: 'success',
        duration: 4000,
        isClosable: true,
        title: activar ? `${location.name} vuelve a estar disponible` : `${location.name} ya no admite reservas`,
        description: activar
          ? 'Ya aparece en el formulario y en el panel del cliente.'
          : 'Sus reservas y sus informes siguen intactos.',
      });
    } catch (e) {
      toast({
        status: 'error',
        duration: 6000,
        isClosable: true,
        title: 'No se pudo cambiar el estado',
        description: (e as Error).message,
      });
    } finally {
      setCambiando('');
      setPorConfirmar(null);
    }
  };

  const getCompanies = async () => {
    const companiesCollection = collection(firebaseFirestore, 'companies');
    const companiesSnapshot = await getDocs(companiesCollection);
    const companiesData = companiesSnapshot.docs.map((doc) => ({
      name: doc.data().name,
      id: doc.id,
    }));
    setCompanies(companiesData);
  };

  const getLocationsByCompany = async () => {
    const companyRef = doc(firebaseFirestore, 'companies', company?.id ?? '');
    const q = query(collection(firebaseFirestore, 'locations'), where('company', '==', companyRef));

    if (onSnaphotRef.current) {
      onSnaphotRef.current.unsubscribe();
    }

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const locationsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLocations(locationsData as Location[]);
    });

    onSnaphotRef.current = {
      unsubscribe,
    };
  };

  useEffect(() => {
    if (company) {
      getLocationsByCompany();
      contarProximas();
    }
  }, [company]);

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <Flex>
      <Flex pr={100} gap={4} mb={4}>
        <FormControl mb={4}>
          <FormLabel>Negocios</FormLabel>
          <Select
            placeholder='Seleccione un negocio'
            onChange={(e) => {
              setCompany(companies.find((company) => company.id === e.target.value));
            }}
          >
            {companies.map((company) => {
              return (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              );
            })}
          </Select>
        </FormControl>
      </Flex>
      <Flex flexDirection={'column'} flex={2} gap={4} mb={4}>
        <Flex>
          <Text>Ubicaciones/sedes </Text>
          <IconButton
            colorScheme='blue'
            size='xs'
            ml={2}
            aria-label='Sedes'
            icon={<AddIcon />}
            onClick={() => {
              onOpen();
            }}
          />
        </Flex>

        {company && (
          <Alert status='info' borderRadius='md' fontSize='sm' alignItems='flex-start'>
            <AlertIcon />
            <Box>
              Al desactivar una sede deja de ofrecerse en el formulario y desaparece del
              panel del cliente. <b>Sus reservas y sus informes no se tocan</b>: siguen
              guardados y se pueden consultar como siempre. Se puede volver a activar
              cuando se quiera.
            </Box>
          </Alert>
        )}

        <TableContainer>
          <Table variant='simple' size='sm'>
            <Thead>
              <Tr>
                <Th>Nombre</Th>
                <Th>Dirección</Th>
                <Th isNumeric>Reservas próximas</Th>
                <Th>Admite reservas</Th>
                <Th>Id</Th>
              </Tr>
            </Thead>
            <Tbody>
              {ordenarSedes(locations).map((location) => {
                const activa = (location as any).status !== 'inactive';
                const futuras = proximas[location.id] ?? 0;
                return (
                  <Tr key={location.id} opacity={activa ? 1 : 0.6}>
                    <Td fontWeight={activa ? 'semibold' : 'normal'}>
                      <HStack spacing={2}>
                        <Text>{location.name}</Text>
                        {!activa && (
                          <Badge colorScheme='gray' fontSize='0.7em'>
                            Desactivada
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td fontSize='xs' color='gray.600'>
                      {location.address}
                    </Td>
                    <Td isNumeric>
                      {cargandoProximas ? (
                        <Spinner size='xs' />
                      ) : futuras > 0 ? (
                        <Badge colorScheme={activa ? 'blue' : 'orange'}>{futuras}</Badge>
                      ) : (
                        <Text fontSize='xs' color='gray.400'>
                          —
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip
                          hasArrow
                          fontSize='xs'
                          label={
                            activa
                              ? 'Desactivar: dejará de admitir reservas nuevas'
                              : 'Activar: volverá a admitir reservas'
                          }
                        >
                          <Box>
                            <Switch
                              size='sm'
                              colorScheme='green'
                              isChecked={activa}
                              isDisabled={cambiando === location.id}
                              onChange={() => {
                                // Activar no rompe nada; desactivar sí puede dejar
                                // gente colgada, así que ese sí se confirma.
                                if (activa) setPorConfirmar(location);
                                else cambiarEstado(location, true);
                              }}
                            />
                          </Box>
                        </Tooltip>
                        {cambiando === location.id && <Spinner size='xs' />}
                      </HStack>
                    </Td>
                    <Td fontSize='10px' color='gray.400'>
                      {location.id}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
      <LocationModal
        isOpen={isOpen}
        onClose={onClose}
        selectedUser={selectedUser}
        company={company}
      />

      <Modal isOpen={Boolean(porConfirmar)} onClose={() => setPorConfirmar(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize='md'>Desactivar {porConfirmar?.name}</ModalHeader>
          <ModalBody>
            <Text fontSize='sm' mb={3}>
              Dejará de ofrecerse en el formulario y desaparecerá del panel del cliente.
            </Text>
            {(proximas[porConfirmar?.id ?? ''] ?? 0) > 0 && (
              <Alert status='warning' borderRadius='md' fontSize='sm' mb={3}>
                <AlertIcon />
                <Box>
                  Tiene <b>{proximas[porConfirmar?.id ?? '']} reservas por venir</b>. No se
                  cancelan ni se pierden, pero esas personas ya no podrán cambiarlas de hora
                  ni reservar otra vez aquí.
                </Box>
              </Alert>
            )}
            <Text fontSize='sm' color='gray.600'>
              Las reservas antiguas y los informes no se tocan. Se puede volver a activar en
              cualquier momento.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant='ghost' size='sm' onClick={() => setPorConfirmar(null)}>
              Cancelar
            </Button>
            <Button
              size='sm'
              colorScheme='red'
              isLoading={cambiando === porConfirmar?.id}
              onClick={() => porConfirmar && cambiarEstado(porConfirmar, false)}
            >
              Desactivar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

/** Las activas primero y, dentro de cada grupo, por nombre. */
const ordenarSedes = (lista: Location[]): Location[] =>
  [...lista].sort((a, b) => {
    const ia = (a as any).status === 'inactive' ? 1 : 0;
    const ib = (b as any).status === 'inactive' ? 1 : 0;
    if (ia !== ib) return ia - ib;
    return String(a.name ?? '').localeCompare(String(b.name ?? ''), 'es');
  });

const LocationModal = ({ isOpen, onClose, selectedUser, company }: any) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInputs>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const onHandleCreateLocation = async (data: IFormInputs) => {
    setIsLoading(true);

    const companyRef = doc(firebaseFirestore, 'companies', company.id);

    // create document in locations by company
    await addDoc(collection(firebaseFirestore, 'locations'), {
      name: data.name,
      address: data.address,
      company: companyRef,
      status: 'active',
    });

    setIsLoading(false);
    onClose();
    reset();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onHandleCreateLocation)}>
          <ModalHeader>{'Crear un nueva ubicación'}</ModalHeader>

          <ModalBody gap={4}>
            <FormControl mb={4} isInvalid={handleIsInvalidField(errors.name?.message)}>
              <FormLabel>Nombre de la sede</FormLabel>
              <Input type='text' {...register('name')} />
            </FormControl>

            <FormControl mb={4} isInvalid={handleIsInvalidField(errors.address?.message)}>
              <FormLabel>Dirección</FormLabel>
              <Input type='text' {...register('address')} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              disabled={!isLoading}
              colorScheme='gray'
              mr={3}
              onClick={() => {
                onClose();
                reset();
              }}
            >
              Cancelar
            </Button>
            <Button isLoading={isLoading} type='submit' colorScheme='blue'>
              {selectedUser ? 'Save' : 'Crear'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export interface IFormInputs {
  name: string;
  address: string;
}
export const schema = yup.object({
  name: yup.string().required('Requerido'),
  address: yup.string().required('Requerido'),
});
export default LocationsSettings;
